import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Radio, X, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

const DISMISS_KEY = "wk_live_dismissed";
const NOTIFIED_KEY = "wk_live_notified";

export default function LiveBanner() {
  const [live, setLive] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const notifiedRef = useRef(new Set(JSON.parse(sessionStorage.getItem(NOTIFIED_KEY) || "[]")));

  useEffect(() => {
    // Restore dismiss state (session-only)
    setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1");
    let cancel = false;
    const poll = async () => {
      try {
        const r = await api.get("/notifications/live");
        if (cancel) return;
        const next = r.data.live || null;
        setLive((prev) => {
          if (next && (!prev || prev.stream_id !== next.stream_id)) {
            // just went live — reset dismiss + fire browser notification
            sessionStorage.removeItem(DISMISS_KEY);
            setDismissed(false);
            if (!notifiedRef.current.has(next.stream_id) && typeof window !== "undefined") {
              notifiedRef.current.add(next.stream_id);
              sessionStorage.setItem(NOTIFIED_KEY, JSON.stringify([...notifiedRef.current]));
              try {
                if ("Notification" in window && Notification.permission === "granted") {
                  new Notification("Wolfkeeper is LIVE with Toriano", {
                    body: next.title,
                    icon: "/favicon-wolf.png",
                  });
                }
              } catch {}
            }
          }
          return next;
        });
      } catch {}
    };
    poll();
    const t = setInterval(poll, 20000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  const close = () => { sessionStorage.setItem(DISMISS_KEY, "1"); setDismissed(true); };

  return (
    <AnimatePresence>
      {live && !dismissed && (
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -50, opacity: 0 }}
          data-testid="live-banner"
          className="fixed top-[72px] left-0 right-0 z-40 bg-red-600/90 text-white backdrop-blur"
        >
          <div className="mx-auto max-w-[1400px] px-6 py-2.5 flex items-center gap-4 text-sm">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
              <Radio size={14} />
              <b>LIVE NOW</b>
            </span>
            <span className="truncate">
              Toriano is on air — <span className="italic">{live.title}</span>
            </span>
            <Link to="/livestream" className="ml-auto inline-flex items-center gap-1 underline underline-offset-4 font-semibold">
              Join the stream <ExternalLink size={12} />
            </Link>
            <button onClick={close} aria-label="Dismiss" className="opacity-70 hover:opacity-100">
              <X size={14} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
